import { GroupsOutlined, PaymentsOutlined, SchoolOutlined, WorkspacePremiumOutlined } from "@mui/icons-material";
import { alpha, Box, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import { type FC, type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useTeamRoster } from "../../../hooks/useTeamRoster";
import { TrainingWageType } from "../../../pages/Settings/DepartmentConfiguration/forms/types";
import { ALPHA, CONTROL_HEIGHT, FONT_SIZE, INK, SHADOW } from "../../../theme/tokens";
import type { WageSettingsResponse } from "../../../types/wageRules";
import { FLAT_RATE_WAGE_MAX, quantizeTrainingOrientationWage, type TrainingOrientationWageBody } from "../../../utils/DepartmentWage.config";
import { isNurseActiveInDepartment } from "../../../utils/formatters";
import { NurseTrainingStatus } from "../../../utils/models";
import { deepLinkWashOverlayStyle } from "../../UIToolkit/Animations/deepLinkWash";
import { MetaChip } from "../../UIToolkit/Badges/MetaChip";
import { isWageAmountValid, parseWageAmount, WageAmountAndModeField, type WageModeOption } from "../../UIToolkit/Inputs/WageAmountAndModeField";
import { AccentIconTile } from "../../UIToolkit/molecules/AccentIconTile";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import { SettingsSection } from "../../UIToolkit/molecules/SettingsSection";
import { matchTrainingPayCards, type TrainingPayCard } from "./deepLinkRowMatchers";
import { useSingleFlightSave } from "./useSingleFlightSave";
import { useSaveTrainingOrientationWage } from "./useTrainingOrientationWage";
import { useWageRuleDeepLink, type WageRuleDeepLink } from "./useWageRuleDeepLink";
import { WageSettingsSaveBar } from "./WageSettingsSaveBar";

/** The backend rejects a percentage outside 0–100; the field validates against the same bound. */
const MAX_WAGE_PERCENTAGE = 100;

/** Names listed in the "who does this land on" hover before it collapses to "+N more". */
const MAX_NAMES_IN_TOOLTIP = 12;

/** Decimal places a dollar rate is stated to, so "$16" and "$16.50" line up in the resulting-rate chip. */
const RATE_DECIMALS = 2;

/** The cards in render order, as a deep link addresses them: by the status whose pay each sets. */
const TRAINING_PAY_CARDS: readonly TrainingPayCard[] = [{ trainingStatus: NurseTrainingStatus.Orientation }, { trainingStatus: NurseTrainingStatus.Trainee }];

type WageDraft = {
	orientationAmount: string;
	orientationType: TrainingWageType;
	trainingAmount: string;
	trainingType: TrainingWageType;
};

const toDraft = (stored: TrainingOrientationWageBody): WageDraft => ({
	orientationAmount: String(stored.orientationWageValue),
	orientationType: stored.orientationWageType,
	trainingAmount: String(stored.trainingWageValue),
	trainingType: stored.trainingWageType,
});

// DEFAULT: what the cards hold while the department's read is in flight. Never shown as a value (the
// amounts are blank, so no resulting rate renders) and never saveable (inputs and Save are locked until
// the read lands, which re-seeds the draft) — it exists only because the mode toggle needs SOME mode.
const LOADING_DRAFT: WageDraft = {
	orientationAmount: "",
	orientationType: TrainingWageType.FlatRate,
	trainingAmount: "",
	trainingType: TrainingWageType.FlatRate,
};

/**
 * DAT-783 · The department's orientation and training wages, edited from the Team page.
 *
 * This is the ONLY edit surface for these two values. They used to sit in Settings → Department
 * Configuration → Rates & Rules, which is registered `access: SUPPORT`, so 99% of customers could
 * never open it and 207 of 209 buildings still carry an inherited $16/hr flat rate. The Settings
 * inputs were removed rather than duplicated: that page saves the whole config snapshot through one
 * unconditional row update, so two live surfaces would mean a Settings save silently reverting an
 * edit made here.
 *
 * Explicit Save, not commit-on-blur: the two rates are a pair, and every save that changes them queues a
 * department-wide wage recalculation over every shift, past and future.
 *
 * The stored pair comes from the tab's own read (`useWageSettings`), never `chosenDepartment`: for any
 * department but the token's, that carries the DEFAULT wage config, so the card would show $16 as if
 * it were saved. Until the read lands, the inputs and Save are locked like the rule tables.
 */
export const TrainingOrientationSection: FC<{
	settings: WageSettingsResponse | undefined;
	isReadOnly: boolean;
	/** Set when a deep link (a trainee's or orientee's wage breakdown) points here: scrolls here and highlights that card. */
	deepLink: WageRuleDeepLink;
}> = ({ settings, isReadOnly, deepLink }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { roster } = useTeamRoster(chosenDepartment.department_id);
	const formatPersonName = useFormatPersonName();

	const departmentWageConfig = settings?.departmentWageConfig;
	const saved = useMemo<TrainingOrientationWageBody | undefined>(
		() =>
			departmentWageConfig && {
				orientationWageType: departmentWageConfig.orientationWageType,
				orientationWageValue: departmentWageConfig.orientationWageValue,
				trainingWageType: departmentWageConfig.trainingWageType,
				trainingWageValue: departmentWageConfig.trainingWageValue,
			},
		[departmentWageConfig],
	);
	const isLoaded = saved !== undefined;
	const { sectionRef, highlightedRowIndices, isTarget } = useWageRuleDeepLink(deepLink, isLoaded, TRAINING_PAY_CARDS, matchTrainingPayCards);
	const isCardHighlighted = (trainingStatus: NurseTrainingStatus): boolean =>
		TRAINING_PAY_CARDS.some((card, index) => card.trainingStatus === trainingStatus && highlightedRowIndices.has(index));
	const savedDraft = useMemo(() => (saved === undefined ? undefined : toDraft(saved)), [saved]);
	// Same seeding rule as the rule tables' draft: while the draft still matches what it was last seeded
	// from (untouched), a newer stored pair re-seeds it — the read arriving after mount must not leave the
	// loading placeholder behind. Adjusted during render, so no frame paints the stale draft.
	const [baseline, setBaseline] = useState<WageDraft | undefined>(savedDraft);
	const [draft, setDraft] = useState<WageDraft>(savedDraft ?? LOADING_DRAFT);
	if (savedDraft !== undefined && !isEqual(baseline, savedDraft) && isEqual(draft, baseline ?? LOADING_DRAFT)) {
		setBaseline(savedDraft);
		setDraft(savedDraft);
	}

	const saveWages = useSaveTrainingOrientationWage();

	const wageModeOptions = useMemo<WageModeOption<TrainingWageType>[]>(
		() => [
			{
				value: TrainingWageType.FlatRate,
				label: t("department.config.rates.wageMode.flatRate"),
				unit: "$",
				// Without it an over-range amount only fails at the server, as a snackbar.
				maxValue: FLAT_RATE_WAGE_MAX,
				maxValueHelperText: t("team.wageSettings.flatRateExceeded"),
			},
			{
				value: TrainingWageType.Percentage,
				label: t("department.config.rates.wageMode.percentage"),
				unit: "%",
				maxValue: MAX_WAGE_PERCENTAGE,
				maxValueHelperText: t("department.config.rates.wageMode.percentageExceeded"),
			},
		],
		[t],
	);
	const optionFor = useCallback((type: TrainingWageType) => wageModeOptions.find((option) => option.value === type), [wageModeOptions]);

	/** The resulting rate, restated as the reader will experience it ("$16.00 / hr", "80% of base wage"). */
	const resultingRate = useCallback(
		(amount: string, type: TrainingWageType): string | undefined => {
			const parsed = parseWageAmount(amount);
			if (parsed === undefined) return undefined;
			return type === TrainingWageType.FlatRate
				? t("team.wageSettings.rate.flatRate", { amount: parsed.toFixed(RATE_DECIMALS) })
				: t("team.wageSettings.rate.percentage", { amount: String(parsed) });
		},
		[t],
	);

	// Who each rate lands on TODAY. Read off the roster the Team page already holds, so there's no extra
	// fetch; "active in this department" comes from the shared per-department helper rather than a
	// global flag, which would count someone active in a sibling building. Names, not just a count, so
	// the chip can answer "which employees?" on hover.
	const affectedByStatus = useMemo<Record<NurseTrainingStatus, string[]>>(() => {
		const counts = Object.fromEntries(Object.values(NurseTrainingStatus).map((status) => [status, [] as string[]])) as Record<NurseTrainingStatus, string[]>;
		for (const nurse of roster) {
			if (isNurseActiveInDepartment(nurse, chosenDepartment.department_id)) counts[nurse.trainingStatus].push(formatPersonName(nurse));
		}
		return counts;
	}, [roster, chosenDepartment.department_id, formatPersonName]);

	/** Names for the hover, capped so a large department doesn't produce an unreadable tooltip. */
	const affectedNamesTooltip = useCallback(
		(names: string[]): string => {
			const shown = names.slice(0, MAX_NAMES_IN_TOOLTIP);
			const remaining = names.length - shown.length;
			return remaining > 0 ? `${shown.join(", ")}${t("team.wageSettings.affected.more", { count: remaining })}` : shown.join(", ");
		},
		[t],
	);

	const orientationOption = optionFor(draft.orientationType);
	const trainingOption = optionFor(draft.trainingType);
	const isValid =
		orientationOption !== undefined &&
		trainingOption !== undefined &&
		isWageAmountValid(draft.orientationAmount, orientationOption) &&
		isWageAmountValid(draft.trainingAmount, trainingOption);

	const isDirty =
		saved !== undefined &&
		(parseWageAmount(draft.orientationAmount) !== saved.orientationWageValue ||
			draft.orientationType !== saved.orientationWageType ||
			parseWageAmount(draft.trainingAmount) !== saved.trainingWageValue ||
			draft.trainingType !== saved.trainingWageType);

	const canSave = isLoaded && isValid && isDirty && !isReadOnly && !saveWages.isPending;
	const disabledReason = isReadOnly ? t("team.wageSettings.readOnly") : !isLoaded ? t("team.wageSettings.rules.notLoaded") : undefined;

	const handleDiscard = useCallback(() => {
		if (savedDraft !== undefined) setDraft(savedDraft);
	}, [savedDraft]);

	const buildBody = useCallback((): TrainingOrientationWageBody | undefined => {
		const orientationWageValue = parseWageAmount(draft.orientationAmount);
		const trainingWageValue = parseWageAmount(draft.trainingAmount);
		if (orientationWageValue === undefined || trainingWageValue === undefined) return undefined;
		return {
			orientationWageType: draft.orientationType,
			orientationWageValue,
			trainingWageType: draft.trainingType,
			trainingWageValue,
		};
	}, [draft]);

	const reseedDraft = useCallback((body: TrainingOrientationWageBody) => {
		// Re-seed from what the server actually stored. The column keeps two places, so a typed
		// `16.999` persists as `17.00`; leaving the draft on `16.999` would hold the form permanently
		// dirty against a `saved` that already agrees.
		const stored = toDraft(quantizeTrainingOrientationWage(body));
		setBaseline(stored);
		setDraft(stored);
	}, []);

	const { mutate: mutateWages } = saveWages;
	const sendRequest = useCallback(
		(body: TrainingOrientationWageBody, release: () => void) => {
			mutateWages(body, { onSuccess: () => reseedDraft(body), onSettled: release });
		},
		[mutateWages, reseedDraft],
	);
	const sendSave = useSingleFlightSave(sendRequest);
	const handleSave = useCallback(() => {
		const body = buildBody();
		if (body !== undefined) sendSave(body);
	}, [buildBody, sendSave]);

	const renderCard = (params: {
		icon: ReactNode;
		label: string;
		tooltip: string;
		amount: string;
		type: TrainingWageType;
		trainingStatus: NurseTrainingStatus;
		onAmountChange: (amount: string) => void;
		onTypeChange: (type: TrainingWageType) => void;
		dataTestId: string;
	}) => {
		const rate = resultingRate(params.amount, params.type);
		const affectedNames = affectedByStatus[params.trainingStatus];
		const affected = affectedNames.length;
		const highlighted = isCardHighlighted(params.trainingStatus);
		return (
			<Box
				data-testid={`${params.dataTestId}-card`}
				data-highlighted={highlighted ? "true" : undefined}
				sx={[
					(theme) => ({
						// Always positioned, so the highlight overlay's `::after` anchors here without shifting layout.
						position: "relative",
						p: 2,
						borderRadius: 1,
						border: `1px solid ${alpha(theme.palette.primary.main, ALPHA.BRAND_TINT)}`,
						background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, ALPHA.HERO_WASH)} 0%, ${alpha(theme.palette.primary.main, ALPHA.HOVER_OVERLAY)} 100%)`,
						boxShadow: SHADOW.PANEL_SOFT,
						minWidth: 0,
					}),
					// An overlay, not a background colour: the card's gradient would paint over an animated one.
					highlighted && deepLinkWashOverlayStyle,
				]}
			>
				<WageAmountAndModeField<TrainingWageType>
					icon={<AccentIconTile size="xs">{params.icon}</AccentIconTile>}
					label={params.label}
					tooltip={params.tooltip}
					amount={params.amount}
					onAmountChange={params.onAmountChange}
					mode={params.type}
					onModeChange={params.onTypeChange}
					modeOptions={wageModeOptions}
					invalidHelperText={t("team.wageSettings.amountInvalid")}
					// Also locked until the stored pair arrives: an edit made before then would be typed over the
					// loading placeholder rather than the department's real rates.
					disabled={isReadOnly || saveWages.isPending || !isLoaded}
					dataTestId={params.dataTestId}
					hint={
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minHeight: CONTROL_HEIGHT.MICRO }}>
							<Typography component="span" sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 600, letterSpacing: "0.04em", color: INK.MUTED }}>
								{t("team.wageSettings.rate.lead")}
							</Typography>
							{rate !== undefined && <MetaChip tone="primary" label={rate} icon={<PaymentsOutlined />} dataTestId={`${params.dataTestId}-resulting-rate`} />}
							{/* Who the rate lands on right now — the abstract setting made concrete, and the honest
							    empty state when nobody in the department currently carries this training status. */}
							<MetaChip
								tone="neutral"
								icon={<GroupsOutlined />}
								label={affected === 0 ? t("team.wageSettings.affected.none") : t("team.wageSettings.affected.count", { count: affected })}
								// Naming them turns "3 employees" into something checkable. Capped, because a
								// department can carry dozens and a tooltip that long is unreadable.
								tooltip={affected === 0 ? undefined : affectedNamesTooltip(affectedNames)}
								dataTestId={`${params.dataTestId}-affected`}
							/>
						</Box>
					}
				/>
			</Box>
		);
	};

	return (
		<Box ref={sectionRef} data-deep-link-target={isTarget ? "true" : undefined} sx={{ width: "100%" }}>
			<SettingsSection
				headerPlacement="side"
				icon={<PaymentsOutlined />}
				title={t("team.wageSettings.title")}
				description={t("team.wageSettings.description")}
				layout="stack"
				contentMaxWidth={880}
				dataTestId="team-wage-settings"
			>
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, width: "100%" }}>
					{renderCard({
						icon: <SchoolOutlined />,
						label: t("team.wageSettings.orientation.label"),
						tooltip: t("team.wageSettings.orientation.tooltip"),
						amount: draft.orientationAmount,
						type: draft.orientationType,
						trainingStatus: NurseTrainingStatus.Orientation,
						onAmountChange: (orientationAmount) => setDraft((prev) => ({ ...prev, orientationAmount })),
						onTypeChange: (orientationType) => setDraft((prev) => ({ ...prev, orientationType })),
						dataTestId: "team-wage-settings-orientation",
					})}
					{renderCard({
						icon: <WorkspacePremiumOutlined />,
						label: t("team.wageSettings.training.label"),
						tooltip: t("team.wageSettings.training.tooltip"),
						amount: draft.trainingAmount,
						type: draft.trainingType,
						trainingStatus: NurseTrainingStatus.Trainee,
						onAmountChange: (trainingAmount) => setDraft((prev) => ({ ...prev, trainingAmount })),
						onTypeChange: (trainingType) => setDraft((prev) => ({ ...prev, trainingType })),
						dataTestId: "team-wage-settings-training",
					})}
				</Box>

				<InlineHint tone="info" dataTestId="team-wage-settings-explainer" sx={{ width: "100%" }}>
					{t("team.wageSettings.appliesTo")}
				</InlineHint>

				<WageSettingsSaveBar
					isReadOnly={isReadOnly}
					readOnlyHint={t("team.wageSettings.readOnly")}
					isDirty={isDirty}
					canSave={canSave}
					isPending={saveWages.isPending}
					disabledReason={disabledReason}
					onDiscard={handleDiscard}
					onSave={handleSave}
					saveLabel={t("team.wageSettings.save")}
					discardLabel={t("team.wageSettings.discard")}
					dataTestId="team-wage-settings"
				/>
			</SettingsSection>
		</Box>
	);
};
